import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteBroadcast } from 'api/broadcasts.service';
import { useToast } from 'hooks/ui';
import {
  resetDraft,
  setDraftBroadcast,
  useBroadcastStore,
} from 'store/broadcast';
import { useApplicationStore } from 'store/application';
import { broadcastRoutes } from 'router/broadcastRoutes';
import { Broadcast } from 'models/broadcast.model';
import { broadcastKeys } from './queryKeys';

export const useBroadcastDeleteQuery = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const inProgressBroadcasts = useBroadcastStore(
    (state) => state.inProgressBroadcasts,
  );
  const { setLoading, removeLoading } = useApplicationStore();

  const discardDraft = (): void => {
    resetDraft();

    if (inProgressBroadcasts.length) {
      setDraftBroadcast(inProgressBroadcasts[0]);
      navigate(broadcastRoutes.broadcastId(inProgressBroadcasts[0]?.id));

      return;
    }

    navigate(broadcastRoutes.base);
  };

  const { isPending, isError, mutate } = useMutation({
    mutationFn: (id: string) => {
      setLoading();

      return deleteBroadcast(id);
    },
    onSuccess: ({ id }: Broadcast) => {
      queryClient.invalidateQueries({ queryKey: broadcastKeys.all });
      toast.success('Broadcast has been deleted successfully');

      const nextBroadcast = inProgressBroadcasts.find(
        (br: Broadcast) => br.id !== id,
      );

      if (nextBroadcast) {
        navigate(broadcastRoutes.broadcastId(nextBroadcast.id));
        setDraftBroadcast(nextBroadcast);
      } else {
        navigate(broadcastRoutes.notFound);
        resetDraft();
      }
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => removeLoading(),
  });

  return { isLoading: isPending, isError, mutate, discardDraft };
};
